import { isSafeObjectKey } from "@ease/shared";

// ---------------------------------------------------------------------------
// AI Output Sanitizer
// ---------------------------------------------------------------------------
// Strips potentially dangerous content from AI-generated Puck JSON.
// Runs before storing or rendering any AI output.
//
// Defense-in-depth: This is ONE layer. CSP headers (csp-builder.ts) provide
// the second layer. Even if a bypass slips through here, CSP blocks execution.
//
// Known limitations of regex-based sanitization:
// - Not a full HTML parser — edge cases may exist
// - Relies on CSP as backstop for any bypasses
// - For user-submitted HTML (not AI output), consider a parser-based
//   sanitizer like DOMPurify if available in the runtime

/**
 * Pre-processing: strip null bytes and normalize HTML entities that could
 * encode dangerous patterns. Runs before all other sanitization.
 */
function preProcess(value: string): string {
  let result = value;

  // Strip null bytes (can bypass regex in some contexts)
  result = result.replace(/\0/g, "");

  // Strip Unicode bidirectional control characters (visual spoofing prevention)
  result = result.replace(/[\u202A-\u202E\u2066-\u2069\u200F\u061C]/g, "");

  // Strip ASCII control characters (TAB, LF, CR, VT, FF, etc.) from inside
  // URL-scheme-like substrings \u2014 e.g. `java\nscript:` would otherwise slip
  // past the `/javascript\s*:/` matcher even though browsers tolerate it in
  // attribute values. We only collapse control chars that sit between two
  // ASCII letters or between an ASCII letter and a colon to avoid mangling
  // formatted prose.
  result = result.replace(
    /([A-Za-z])[\x00-\x1F]+(?=[A-Za-z:])/g,
    (_m, c: string) => c,
  );

  // Decode HTML entities that encode dangerous characters
  // &#x6A; &#106; → 'j', &#x3C; &#60; → '<', etc.
  result = result.replace(/&#x([0-9a-fA-F]+);?/g, (_match, hex) =>
    String.fromCharCode(parseInt(hex, 16)),
  );
  result = result.replace(/&#(\d+);?/g, (_match, dec) =>
    String.fromCharCode(parseInt(dec, 10)),
  );

  return result;
}

/**
 * HTML tags that are never allowed in Puck component props.
 * We target execution vectors — not structural HTML (forms are handled by CSP).
 * Each pattern matches the full opening tag (with attributes) and,
 * for tags with content, the content + closing tag as well.
 */
const DANGEROUS_TAG_PATTERNS = [
  /<script[^>]*>[\s\S]*?<\/script>/gi,
  /<script[^>]*\/?>/gi,
  /<iframe[^>]*>[\s\S]*?<\/iframe>/gi,
  /<iframe[^>]*\/?>/gi,
  /<object[^>]*>[\s\S]*?<\/object>/gi,
  /<object[^>]*\/?>/gi,
  /<embed[^>]*\/?>/gi,
  /<svg[^>]*>[\s\S]*?<\/svg>/gi,
  /<svg[^>]*\/?>/gi,
  /<math[^>]*>[\s\S]*?<\/math>/gi,
  /<math[^>]*\/?>/gi,
  /<base[^>]*\/?>/gi,
  /<link[^>]*\/?>/gi,
  /<meta[^>]*\/?>/gi,
] as const;

/**
 * URI schemes that can execute code.
 */
const DANGEROUS_URI_PATTERNS = [
  /javascript\s*:/gi,
  /vbscript\s*:/gi,
  /data:\s*text\/html/gi,
  /data:\s*application\/javascript/gi,
  /data:\s*image\/svg\+xml/gi,
  /data:\s*text\/xml/gi,
  /data:\s*application\/xhtml\+xml/gi,
] as const;

/**
 * Specific HTML event handler attributes (not the broad `on\w+` which false-positives).
 * This list covers the most common XSS vectors.
 */
const DANGEROUS_EVENT_HANDLERS = new Set([
  "onclick",
  "ondblclick",
  "onmousedown",
  "onmouseup",
  "onmouseover",
  "onmouseout",
  "onmousemove",
  "onkeydown",
  "onkeyup",
  "onkeypress",
  "onfocus",
  "onblur",
  "onchange",
  "onsubmit",
  "onreset",
  "onselect",
  "onload",
  "onunload",
  "onerror",
  "onabort",
  "onbeforeunload",
  "onhashchange",
  "onpageshow",
  "onpagehide",
  "onresize",
  "onscroll",
  "oninput",
  "oncontextmenu",
  "ondrag",
  "ondragend",
  "ondragenter",
  "ondragleave",
  "ondragover",
  "ondragstart",
  "ondrop",
  "onanimationstart",
  "onanimationend",
  "ontransitionend",
  "onpointerdown",
  "onpointerup",
  "onpointermove",
  "ontouchstart",
  "ontouchend",
  "ontouchmove",
]);

/**
 * Source string for event handler regex — compiled locally per call to avoid
 * stateful /g flag issues across multiple invocations.
 */
const EVENT_HANDLER_SOURCE = `(${[...DANGEROUS_EVENT_HANDLERS].join("|")})\\s*=\\s*`;

/**
 * Sanitize a string value from AI output — strip dangerous HTML/JS patterns.
 */
export function sanitizeString(value: string): string {
  // Pre-process: decode entities, strip null bytes (loop until stable, max 3 iterations)
  let result = value;
  for (let i = 0; i < 3; i++) {
    const prev = result;
    result = preProcess(result);
    if (result === prev) break;
  }

  for (const pattern of DANGEROUS_TAG_PATTERNS) {
    result = result.replace(new RegExp(pattern.source, pattern.flags), "");
  }

  for (const pattern of DANGEROUS_URI_PATTERNS) {
    result = result.replace(new RegExp(pattern.source, pattern.flags), "");
  }

  // Create fresh regex per call to avoid stateful /g flag issues
  const eventHandlerRe = new RegExp(EVENT_HANDLER_SOURCE, "gi");
  result = result.replace(eventHandlerRe, "");

  return result;
}

/**
 * Recursively sanitize all string values in an object (Puck JSON props).
 * Returns a new object — does not mutate the original.
 */
export function sanitizePuckData(
  data: Record<string, unknown>,
): Record<string, unknown> {
  return sanitizeValue(data) as Record<string, unknown>;
}

function sanitizeValue(value: unknown): unknown {
  if (typeof value === "string") {
    return sanitizeString(value);
  }

  if (Array.isArray(value)) {
    return value.map(sanitizeValue);
  }

  if (value !== null && typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      // 11.5: Skip prototype pollution keys + dangerous event handlers
      if (!isSafeObjectKey(key) || DANGEROUS_EVENT_HANDLERS.has(key.toLowerCase())) {
        continue;
      }

      result[key] = sanitizeValue(val);
    }
    return result;
  }

  return value;
}

/**
 * Check if a string contains any dangerous patterns.
 * Returns list of detected threats (empty = safe).
 */
export function detectThreats(value: string): string[] {
  const threats: string[] = [];

  // Pre-process to catch encoded threats (loop until stable, max 3 iterations)
  let processed = value;
  for (let i = 0; i < 3; i++) {
    const prev = processed;
    processed = preProcess(processed);
    if (processed === prev) break;
  }

  for (const pattern of DANGEROUS_TAG_PATTERNS) {
    if (new RegExp(pattern.source, pattern.flags).test(processed)) {
      threats.push(`dangerous_tag:${pattern.source}`);
    }
  }

  for (const pattern of DANGEROUS_URI_PATTERNS) {
    if (new RegExp(pattern.source, pattern.flags).test(processed)) {
      threats.push(`dangerous_uri:${pattern.source}`);
    }
  }

  // Create fresh regex per call to avoid stateful /g flag issues
  const eventHandlerRe = new RegExp(EVENT_HANDLER_SOURCE, "gi");
  if (eventHandlerRe.test(processed)) {
    threats.push("event_handler");
  }

  return threats;
}
